import { PlayerInterface } from "@foundation-player/api";
import { MEDIA_INIT_EVENT } from "./events.js";
import { AudioPlayer } from "./media_audio.js";
import { VideoPlayer } from "./media_video.js";

type Mode = "audio" | "video";

const template = document.createElement("template");

template.innerHTML = /* HTML */ `
  <style>
    :host {
      display: block;
    }

    .player-wrapper.is-fullscreen {
      align-items: center;
      background-color: #000000;
      block-size: 100dvb;
      display: flex;
      inline-size: 100dvi;
      inset: 0 0 auto auto;
      justify-content: center;
      position: fixed;
      z-index: 2147483647;
    }

    .player-container {
      position: relative;
    }

    .player-container[data-mode="video"] {
      aspect-ratio: 16/9;
      inline-size: 100%;
    }

    .player-container [data-mode="audio"][role="region"] {
      background-color: var(--color-bg-subtle);
    }
  </style>
  <div class="player-wrapper">
    <div class="player-container"></div>
  </div>
`;

export class MediaPlayer extends HTMLElement {
  player: PlayerInterface | undefined;
  #playerContainer: HTMLElement | undefined;

  get userPlus(): boolean {
    return this.dataset.userPlus === "true";
  }

  get freeAccessibleSeconds(): number | undefined {
    const freeAccSec = this.dataset.freeAccessibleSeconds;
    const parsed = parseInt(freeAccSec ?? "", 10);
    return isNaN(parsed) ? undefined : parsed;
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.shadowRoot!.appendChild(template.content.cloneNode(true));
  }

  #getPlayerComponent(mode: Mode) {
    return {
      audio: AudioPlayer,
      video: VideoPlayer,
    }[mode];
  }

  connectedCallback() {
    if (!this.dataset.renditions) {
      console.error("Foundation Player", "Error on player: No renditions");
      return;
    }
    this.#playerContainer = this.shadowRoot!.querySelector(
      ".player-container",
    )! as HTMLElement;
    this.addEventListener(MEDIA_INIT_EVENT, this.#cleanUpElements);
    Object.assign(this.#playerContainer.dataset, this.dataset);
    (async () => {
      await this.#setupMediaElement();
      this.dispatchEvent(new CustomEvent(MEDIA_INIT_EVENT));
    })();
  }

  // https://docs.player.foundation/v2/api/type/player-state/
  // https://docs.player.foundation/v2/api/type/observable/

  #ensureSingularPlayingInstance() {
    if (this.dataset.mode === "audio") {
      this.player!.state.onValueChange("state", ({ state }) => {
        if (state === "playing") {
          const otherPlayers = Array.from(document.querySelectorAll("ws-media"))
            .map(({ player }) => player)
            .filter(Boolean)
            .filter((player) => player !== this.player);
          otherPlayers.forEach((player) => {
            (player as PlayerInterface).pause();
          });
        }
      });
    }
  }

  #handleFreeAccessibleSeconds() {
    if (
      typeof this.freeAccessibleSeconds === "number" &&
      !Number.isNaN(this.freeAccessibleSeconds) &&
      !this.userPlus
    ) {
      this.player!.state.onValueChange(
        "currentTimeInSeconds",
        ({ currentTimeInSeconds }) => {
          if (currentTimeInSeconds > this.freeAccessibleSeconds!) {
            this.player!.pause();
            const dialog = document.querySelector(
              "#paywall-dialog",
            ) as HTMLDialogElement | null;
            if (dialog) {
              dialog.showModal();
            }
          }
        },
      );
    }
  }

  async #setupMediaElement() {
    const PlayerComponent = this.#getPlayerComponent(this.dataset.mode as Mode);
    const playerInstance = new PlayerComponent(this.#playerContainer!);
    await playerInstance.init().catch((reason: unknown) => {
      console.error("Foundation Player", "Error on player:", reason);
    });
    this.player = playerInstance.foundationPlayer;

    this.#handleFreeAccessibleSeconds();
    this.#ensureSingularPlayingInstance();
  }

  disconnectedCallback() {
    this.removeEventListener(MEDIA_INIT_EVENT, this.#cleanUpElements);
  }

  #cleanUpElements() {
    this.querySelector("figure")?.remove();
  }
}

customElements.get("ws-media") ??
  customElements.define("ws-media", MediaPlayer);

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface HTMLElementTagNameMap {
    "ws-media": MediaPlayer;
  }
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    MoltenBundle: any;
  }
}
